import React, { useState, useEffect, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import ImageBoard from './components/ImageBoard';
import NotesSidebar from './components/NotesSidebar';
import ActionButtons from './components/ActionButtons';
import './App.css';

const { FiLink, FiImage, FiUpload, FiAlertCircle } = FiIcons;

function App() {
  const [imageUrl, setImageUrl] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [error, setError] = useState('');
  const boardRef = useRef(null);

  // Helper to convert blob/file to Base64 (solves CORS for html-to-image)
  const toBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  // Handle pasting images from clipboard
  useEffect(() => {
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const blob = items[i].getAsFile();
          try {
            const base64 = await toBase64(blob);
            setImageUrl(base64);
            setMarkers([]);
            setError('');
          } catch (err) {
            setError('Kon geplakte afbeelding niet verwerken.');
          }
          break;
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  const fetchScreenshot = async (e) => {
    e.preventDefault();
    if (!urlInput) return;

    setIsLoading(true);
    setError('');
    try {
      let formattedUrl = urlInput.trim();
      if (!/^https?:\/\//i.test(formattedUrl)) {
        formattedUrl = 'https://' + formattedUrl;
      }

      // Using thum.io with a proxy to get raw image data
      const targetUrl = `https://image.thum.io/get/width/1200/crop/800/noanimate/${formattedUrl}`;
      const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(targetUrl)}`;
      
      const response = await fetch(proxyUrl);
      if (!response.ok) throw new Error('Kon afbeelding niet ophalen van de server.');
      
      const blob = await response.blob();
      const base64 = await toBase64(blob);
      
      setImageUrl(base64);
      setMarkers([]);
    } catch (err) {
      setError('Screenshot mislukt. Tip: Gebruik een volledige URL of plak de afbeelding handmatig (Ctrl+V).');
      console.error('Fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const base64 = await toBase64(file);
        setImageUrl(base64);
        setMarkers([]);
        setError('');
      } catch (err) {
        setError('Uploaden mislukt.');
      }
    }
  };

  const handleAddMarker = (x, y) => {
    const newMarker = {
      id: Date.now(),
      number: markers.length + 1,
      x,
      y,
      text: ''
    };
    setMarkers([...markers, newMarker]);
    setSelectedMarkerId(newMarker.id);
  };

  const updateMarkerText = (id, text) => {
    setMarkers(markers.map(m => m.id === id ? { ...m, text } : m));
  };

  const deleteMarker = (id) => {
    setMarkers(markers.filter(m => m.id !== id).map((m, index) => ({
      ...m,
      number: index + 1
    })));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4 z-10 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="bg-blue-600 p-2 rounded-lg">
            <SafeIcon icon={FiImage} className="text-white text-xl" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 leading-tight">WPHelpdesk screenshot tool</h1>
            <p className="text-xs text-gray-500">Screenshot & Notities</p>
          </div>
        </div>

        <div className="flex items-center gap-4 w-full md:w-auto" idName="wphd-url-input" id="wphd-url-input">
          <form onSubmit={fetchScreenshot} className="flex flex-1 md:w-96">
            <div className="relative flex-1">
              <SafeIcon icon={FiLink} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Website URL (bijv. google.com)..."
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
              />
            </div>
            <button 
              type="submit" 
              disabled={isLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-r-lg text-sm font-medium transition-colors disabled:opacity-70 whitespace-nowrap"
            >
              {isLoading ? 'Laden...' : 'Screenshot'}
            </button>
          </form>

          <div className="hidden md:block text-gray-300">|</div>

          <label className="cursor-pointer bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm whitespace-nowrap">
            <SafeIcon icon={FiUpload} />
            <span>Upload</span>
            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
          </label>
        </div>
      </header>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 m-6 mb-0 flex items-start gap-3 rounded-r-lg">
          <SafeIcon icon={FiAlertCircle} className="text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <main className="flex-1 flex overflow-hidden">
        {imageUrl ? (
          <>
            <div className="flex-1 overflow-auto bg-gray-200 p-6 relative flex justify-center items-start">
              <ImageBoard 
                imageUrl={imageUrl} 
                markers={markers} 
                onAddMarker={handleAddMarker}
                selectedMarkerId={selectedMarkerId}
                setSelectedMarkerId={setSelectedMarkerId}
                boardRef={boardRef}
              />
            </div>
            <div className="w-96 bg-white border-l border-gray-200 flex flex-col shadow-xl z-10">
              <NotesSidebar 
                markers={markers} 
                updateMarkerText={updateMarkerText} 
                deleteMarker={deleteMarker}
                selectedMarkerId={selectedMarkerId}
                setSelectedMarkerId={setSelectedMarkerId}
              />
              <ActionButtons 
                boardRef={boardRef} 
                markers={markers} 
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 text-center">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 flex flex-col items-center">
              <div className="bg-blue-50 p-6 rounded-full mb-6">
                <SafeIcon icon={FiImage} className="text-5xl text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Start met annoteren</h2>
              <p className="max-w-xs text-gray-500 mb-8">
                Maak een screenshot van een website, upload een bestand of plak direct vanuit je klembord.
              </p>
              <div className="flex gap-3">
                <div className="px-4 py-2 bg-gray-100 rounded-lg text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Ctrl + V om te plakken
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;